// Staff Management Component - Admin only
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Avatar,
  Menu,
  MenuItem as MenuOption,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import authService from '../../services/auth';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [tenantId, setTenantId] = useState(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    is_stylist: false,
    role_id: '',
    status: 'active',
    password: '',
    send_welcome_email: false,
  });

  // Check if selected role is General Staff (no login required)
  const isGeneralStaff = () => {
    const selectedRole = roles.find((role) => Number(role.id) === Number(formData.role_id));
    return selectedRole?.name === 'General Staff';
  };

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (authService.isAuthenticated()) {
      // Get tenant_id from current user
      const currentUser = authService.getUser();
      if (currentUser && currentUser.tenant_id) {
        setTenantId(currentUser.tenant_id);
      }
      fetchUsers();
      fetchRoles();
    } else {
      setError('Please log in to access staff management');
      setLoading(false);
    }
    // Intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/admin/users');
      setUsers(response.data || []);
      console.log("fetchUsers response",response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      
      // Handle different types of errors more gracefully
      if (error.message === 'Authorization header required' || error.message === 'Session expired') {
        setError('Please log in to access staff management');
      } else if (error.message === 'Permission denied') {
        setError('You do not have permission to view staff members');
      } else if (error.message === 'API endpoint not available') {
        setError('Staff management is currently unavailable');
      } else {
        setError('Failed to fetch staff: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService.get('/roles');
      const fetched = response.data || [];
      setRoles(fetched);

      // If the current Role filter points to Admin (or any role not in the filtered dropdown),
      // reset it back to "all" so the UI doesn't get stuck showing 0 results.
      try {
        if (selectedRole !== 'all') {
          const selectedId = Number(selectedRole);
          const selectedRoleObj = fetched.find((r) => Number(r.id) === selectedId);
          if (selectedRoleObj?.name === 'Admin') {
            setSelectedRole('all');
          }
        }
      } catch (e) {
        // ignore
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      
      // Handle different types of errors more gracefully
      if (error.message === 'Authorization header required' || error.message === 'Session expired') {
        setError('Please log in to access staff management');
      } else if (error.message === 'Permission denied') {
        setError('You do not have permission to view roles');
      } else if (error.message === 'API endpoint not available') {
        setError('Role information is currently unavailable');
      } else {
        setError('Failed to fetch roles: ' + error.message);
      }
    }
  };

  const handleOpenDialog = (user = null) => {
    setEditingUser(user);
    setShowPassword(false);
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: getDisplayEmail(user.email) || '',
        mobile: user.mobile || '',
        is_stylist: isTruthyOne(user.is_stylist),
        role_id: user.role_id || '',
        status: user.status || 'active',
        password: '',
        send_welcome_email: false,
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        mobile: '',
        is_stylist: false,
        role_id: '',
        status: 'active',
        password: '',
        send_welcome_email: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setError('');
    setShowPassword(false);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const checkEmailUniqueness = (email) => {
    if (!email || isGeneralStaff()) return true;
    
    const normalized = String(email).trim().toLowerCase();

    // Compare against stored emails, but support legacy prefixed format in DB
    const existingUser = users.find((user) => {
      if (!user?.email) return false;
      const stored = String(user.email).trim().toLowerCase();
      const display = String(getDisplayEmail(user.email) || '').trim().toLowerCase();
      return (stored === normalized || display === normalized) && (!editingUser || user.id !== editingUser.id);
    });
    
    return !existingUser;
  };

  const getDisplayEmail = (storedEmail) => {
    if (!storedEmail || !tenantId) return storedEmail;
    // Remove tenant_id prefix for display
    const prefix = `${tenantId}_`;
    return storedEmail.startsWith(prefix) ? storedEmail.substring(prefix.length) : storedEmail;
  };

  // Normalize boolean-ish DB values (e.g. 0/1 or "0"/"1") to a real boolean
  const isTruthyOne = (value) => Number(value) === 1;

  // Check if a user is an admin
  const isUserAdmin = (user) => {
    return user && (user.role_name === 'Admin' || user.role === 'Admin');
  };

  const handleSubmit = async () => {
    try {
      setError('');

      // Validation
      if (!formData.first_name.trim()) {
        setError('First name is required');
        return;
      }

      // Email and password validation based on role
      if (!isGeneralStaff()) {
        if (!formData.email.trim()) {
          setError('Email is required for staff with login access');
          return;
        }
        
        // Check email uniqueness
        if (!checkEmailUniqueness(formData.email)) {
          setError('This email address is already in use. Please use a different email.');
          return;
        }
        
        if (!editingUser && !formData.password) {
          setError('Password is required for new staff with login access');
          return;
        }
      }

      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: isGeneralStaff() ? null : String(formData.email || '').trim(),
        mobile: formData.mobile,
        // Backend validation expects 0/1 (not boolean)
        is_stylist: formData.is_stylist ? 1 : 0,
        // Backend validation expects numeric
        role_id: formData.role_id ? Number(formData.role_id) : formData.role_id,
        status: formData.status,
        send_welcome_email: isGeneralStaff() ? false : formData.send_welcome_email,
      };

      if (formData.password && !isGeneralStaff()) {
        submitData.password = formData.password;
      }

      if (editingUser) {
        await apiService.put(`/admin/users/${editingUser.id}`, submitData);
        setSuccessMessage('Staff member updated successfully!');
      } else {
        await apiService.post('/admin/users', submitData);
        setSuccessMessage('Staff member created successfully!');
      }

      fetchUsers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving staff:', error);
      const rd = error?.responseData;
      const validationErrors = rd?.data?.errors;
      const validationText =
        validationErrors && typeof validationErrors === 'object'
          ? Object.values(validationErrors).filter(Boolean).join(' | ')
          : '';

      const msg =
        validationText ||
        rd?.error ||
        rd?.message ||
        error?.message ||
        'Failed to save staff member';

      setError(msg);
    }
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleDeleteUser = () => {
    console.log('handleDeleteUser called with selectedUser:', selectedUser);
    
    // Prevent deleting admin users
    if (isUserAdmin(selectedUser)) {
      console.log('User is admin, preventing deletion');
      setError('Cannot delete admin users');
      handleMenuClose();
      return;
    }
    
    console.log('Setting showDeleteDialog to true');
    setShowDeleteDialog(true);
    // Don't call handleMenuClose() here as it clears selectedUser
    // We'll close the menu manually by clearing anchorEl
    setAnchorEl(null);
  };

  const confirmDeleteUser = async () => {
    console.log('confirmDeleteUser called with selectedUser:', selectedUser);
    
    if (!selectedUser) {
      console.log('No selectedUser, returning early');
      return;
    }
    
    console.log('Attempting to delete user with ID:', selectedUser.id);
    
    try {
      const response = await apiService.delete(`/admin/users/${selectedUser.id}`);
      console.log('Delete API response:', response);
      
      setSuccessMessage('Staff member deleted successfully!');
      fetchUsers();
      setShowDeleteDialog(false);
      setSelectedUser(null);
      setAnchorEl(null); // Also close any open menu
    } catch (error) {
      console.error('Error deleting staff:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.message || 
                         error.message || 
                         'Failed to delete staff member';
      setError(errorMessage);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteDialog(false);
    setSelectedUser(null);
    setAnchorEl(null); // Also close any open menu
  };

  const getFilteredUsers = () => {
    return users.filter(user => {
      // Hide Admin users from the staff list entirely
      if (isUserAdmin(user)) return false;

      const matchesSearch = 
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (getDisplayEmail(user.email) && getDisplayEmail(user.email).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.mobile && user.mobile.includes(searchTerm));
      
      const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus;
      const matchesRole =
        selectedRole === 'all' || Number(user.role_id) === Number(selectedRole);
      
      return matchesSearch && matchesStatus && matchesRole;
    });
  };

  const getTabCounts = () => {
    const visibleUsers = users.filter((u) => !isUserAdmin(u));
    const activeUsers = visibleUsers.filter(u => u.status === 'active').length;
    const inactiveUsers = visibleUsers.filter(u => u.status === 'inactive').length;
    
    return {
      all: visibleUsers.length,
      active: activeUsers,
      inactive: inactiveUsers,
    };
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : 'No Role';
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  const tabCounts = getTabCounts();
  const filteredUsers = getFilteredUsers();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Staff
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            Add Staff
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Staff
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {tabCounts.all}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Staff
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {tabCounts.active}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BlockIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Inactive Staff
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {tabCounts.inactive}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SecurityIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Roles Available
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {roles.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search staff by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  label="Status"
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={selectedRole}
                  label="Role"
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {roles.filter((role) => role.name !== 'Admin').map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary">
                {filteredUsers.length} of {tabCounts.all} staff
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Staff Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Staff</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Stylist</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box py={4}>
                    <PeopleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No staff found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {searchTerm || selectedStatus !== 'all' || selectedRole !== 'all'
                        ? 'Try adjusting your search or filters'
                        : 'Add your first staff member to get started'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {getInitials(user.first_name, user.last_name)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID: {user.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2">{getDisplayEmail(user.email)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Login: {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      {user.mobile && (
                        <Box display="flex" alignItems="center">
                          <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">{user.mobile}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getRoleName(user.role_id)}
                        size="small"
                        color={isUserAdmin(user) ? 'error' : 'primary'}
                        variant={isUserAdmin(user) ? 'filled' : 'outlined'}
                      />
                      {isUserAdmin(user) && (
                        <Chip
                          label="Protected"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isTruthyOne(user.is_stylist) ? 'Yes' : 'No'}
                      size="small"
                      color={isTruthyOne(user.is_stylist) ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status}
                      size="small"
                      color={getStatusColor(user.status)}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuOption onClick={() => { handleOpenDialog(selectedUser); handleMenuClose(); }}>
          <EditIcon sx={{ mr: 1 }} /> Edit Staff
        </MenuOption>
        <MenuOption 
          onClick={handleDeleteUser} 
          disabled={isUserAdmin(selectedUser)}
          sx={{ 
            color: 'error.main',
            opacity: isUserAdmin(selectedUser) ? 0.5 : 1,
            cursor: isUserAdmin(selectedUser) ? 'not-allowed' : 'pointer'
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} /> 
          {isUserAdmin(selectedUser) ? 'Delete Staff (Admin)' : 'Delete Staff'}
        </MenuOption>
      </Menu>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            {editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          
          
          <Box sx={{ mt: 1 }}>
            {/* Row 1: First Name and Last Name */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
              />
            </Box>

            {/* Row 2: Mobile and Is Stylist */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_stylist}
                    onChange={handleInputChange}
                    name="is_stylist"
                  />
                }
                label="Is a stylist"
                sx={{ minWidth: 'fit-content' }}
              />
            </Box>

            {/* Row 3: Role and Status */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role_id"
                  value={formData.role_id}
                  label="Role"
                  onChange={handleInputChange}
                >
                  {roles.filter(role => role.name !== 'Admin').map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleInputChange}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Row 4: Email and Password (conditional for General Staff) */}
            {!isGeneralStaff() && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  error={formData.email && !checkEmailUniqueness(formData.email)}
                  helperText={
                    formData.email && !checkEmailUniqueness(formData.email) 
                      ? "This email address is already in use" 
                      :   ""
                  }
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  helperText={editingUser ? "Leave blank to keep current password" : ""}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

{formData.email && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Login Information:</strong> New Staff login email is <strong>{String(formData.email || '').trim()}</strong>.
              </Typography>
            </Alert>
          )}

            {/* General Staff Notice */}
            {isGeneralStaff() && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="info.contrastText">
                  <strong>General Staff Notice:</strong> This staff member will not have login access. 
                  They are typically used for housekeeping, cleaning, or other non-system roles.
                </Typography>
              </Box>
            )}

            {/* Row 5: Send Welcome Email (only for new users with login access) */}
            {!editingUser && !isGeneralStaff() && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.send_welcome_email}
                      onChange={handleInputChange}
                      name="send_welcome_email"
                    />
                  }
                  label="Send welcome email to staff member"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.first_name.trim() || (!isGeneralStaff() && !formData.email.trim())}
            sx={{ borderRadius: '8px' }}
          >
            {editingUser ? 'Update Staff' : 'Create Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={cancelDeleteUser}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DeleteIcon sx={{ color: '#f44336', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#d32f2f' }}>
              Delete Staff Member
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              Are you sure you want to delete <strong>{selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : 'this staff member'}</strong>?
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                ⚠️ Warning: This action is permanent and irreversible!
              </Typography>
              <Typography variant="body2" component="div">
                Deleting this staff member will also permanently remove:
                <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                  <li>All their appointment records</li>
                  <li>All invoices they created</li>
                  <li>All expense records they created</li>
                  <li>Their login access and profile data</li>
                </ul>
              </Typography>
            </Alert>
            
            <Alert severity="info" sx={{ borderRadius: '8px' }}>
              <Typography variant="body2">
                💡 <strong>Recommendation:</strong> Consider deactivating the user instead of deleting them to preserve historical data.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button 
            onClick={cancelDeleteUser} 
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              console.log('Delete button clicked');
              confirmDeleteUser();
            }}
            variant="contained"
            color="error"
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#d32f2f'
              }
            }}
          >
            Yes, Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
